import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Money } from '@principle-theorem/accounting';
import { PipesModule } from '@principle-theorem/ng-shared';
import {
  Invoice,
  TransactionAllocation,
} from '@principle-theorem/principle-core';
import {
  TransactionProvider,
  type IInvoice,
  type IStaffer,
} from '@principle-theorem/principle-core/interfaces';
import {
  isSameRef,
  snapshot,
  type DocumentReference,
  type INamedDocument,
  type WithRef,
} from '@principle-theorem/shared';
import { sortBy } from 'lodash';
import { switchMap } from 'rxjs/operators';

export interface IPractitionerAllocationRow {
  practitioner: INamedDocument<IStaffer>;
  discountAmount: number;
  invoicedAmount: number;
  allocatedAmount: number;
  remainingAmount: number;
  isOverAllocated: boolean;
}

@Component({
  selector: 'pr-practitioner-allocation-breakdown',
  imports: [
    CommonModule,
    MatExpansionModule,
    MatIconModule,
    MatTableModule,
    MatTooltipModule,
    PipesModule,
  ],
  templateUrl: './practitioner-allocation-breakdown.component.html',
  styleUrls: ['./practitioner-allocation-breakdown.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PractitionerAllocationBreakdownComponent {
  invoice = input.required<WithRef<IInvoice>>();
  allocations = signal<IPractitionerAllocationRow[]>([]);
  loading = signal<boolean>(true);
  error = signal<string | undefined>(undefined);
  displayedColumns = [
    'practitioner',
    'invoiced',
    'discount',
    'remaining',
    'allocated',
  ];
  hasOverAllocations = computed(() =>
    this.allocations().some((allocation) => allocation.isOverAllocated)
  );

  constructor() {
    toObservable(this.invoice)
      .pipe(
        switchMap(() => this._loadAllocations()),
        takeUntilDestroyed()
      )
      .subscribe((allocations) => this.allocations.set(allocations));
  }

  private async _loadAllocations(): Promise<IPractitionerAllocationRow[]> {
    try {
      this.loading.set(true);
      this.error.set(undefined);

      const invoice = this.invoice();
      const transactions = await snapshot(Invoice.transactions$(invoice));
      const includeDeposits = invoice.includeDepositsInAllocations;

      const practitionerProportions =
        Invoice.getPractitionerProportionsOnInvoice(invoice, includeDeposits);

      const discountTransactions = transactions.filter(
        (transaction) => transaction.provider === TransactionProvider.Discount
      );

      const allAllocations = TransactionAllocation.getInvoiceAllocations(
        invoice,
        transactions
      );

      const nonDiscountAllocations = allAllocations.filter(
        (allocation) =>
          !discountTransactions.some((transaction) =>
            isSameRef(transaction.ref, allocation.transactionRef)
          )
      );

      const discountAllocations = allAllocations.filter((allocation) =>
        discountTransactions.some((transaction) =>
          isSameRef(transaction.ref, allocation.transactionRef)
        )
      );

      const allocationSummaries = TransactionAllocation.getAllocationsSummaries(
        nonDiscountAllocations
      );

      const discountSummaries =
        TransactionAllocation.getAllocationsSummaries(discountAllocations);

      const allocations = practitionerProportions.map((proportion) => {
        const allocated = allocationSummaries.find((summary) => {
          if (!summary.allocatedTo) {
            return false;
          }
          return isSameRef(
            summary.allocatedTo as DocumentReference<IStaffer>,
            proportion.practitioner.ref
          );
        });

        const discounted = discountSummaries.find((summary) => {
          if (!summary.allocatedTo) {
            return false;
          }
          return isSameRef(
            summary.allocatedTo as DocumentReference<IStaffer>,
            proportion.practitioner.ref
          );
        });

        const invoicedAmount = Money.amount(proportion.amount);
        const allocatedAmount = Money.amount(allocated?.allocatedAmount ?? 0);
        const discountAmount = Money.amount(discounted?.allocatedAmount ?? 0);
        const remainingAmount = Money.amount(
          Math.max(0, invoicedAmount - allocatedAmount - discountAmount)
        );
        const isOverAllocated =
          Money.amount(Money.sum([allocatedAmount, discountAmount])) >
          invoicedAmount;

        return {
          practitioner: proportion.practitioner,
          discountAmount,
          invoicedAmount,
          allocatedAmount,
          remainingAmount,
          isOverAllocated,
        };
      });

      return sortBy(allocations, 'practitioner.name');
    } catch {
      this.error.set('Failed to load allocation data');
      return [];
    } finally {
      this.loading.set(false);
    }
  }
}
